import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import { Formik, Form, Field } from 'formik';
import IcomoonReact from 'icomoon-react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';

import NoHoverButton from '../../../shared/components/NoHoverButton';
import Text from '../../../shared/_TBComponents/Text';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import { feedTracker } from '../../../shared/utils/amplitudeHelper';

import {
  resetReportPostInfo,
  resetSubmitSupportError,
  submitSupport,
} from '../ducks/feedActions';

const CloseButtonContainer = styled('div')`
  display: flex;
  justify-content: end;
  align-items: center;
  cursor: pointer;
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 100;
`;

const DividerLine = styled('div')`
  width: calc(100% + 60px);
  margin-left: -30px;
  border-top: 2px solid rgb(232, 232, 232);
  margin-bottom: 30px;
`;

const ErrorText = styled('div')`
  color: #ff6600;
  font-size: 13px;
  line-height: 19.5px;
`;

const FieldError = styled('div')`
  font-size: 14px;
  color: #ff6600;
`;

const FormGroup = styled('div')`
  z-index: 1;
  .form-control {
    min-height: 30px;
    width: 100%;
  }
  .text-area {
    min-height: 100px;
    max-height: 250px;
    overflow: auto;
  }
  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
`;

const ModalContentContainer = styled('div')`
  .submit-button {
    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;
    align-items: center;
    position: absolute;
    margin-left: -30px;
    bottom: 0;
  }
`;

const ModalWrapper = styled('div')`
  display: flex;
  width: 100%;
  position: default;
`;

const SupportTextWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: -10px;
  height: 130px;
`;

const TitlePlusErrorWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const supportSchema = Yup.object().shape({
  body: Yup.string().required('Message is required'),
});

const ReportPostModal = ({ onRequestClose }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const reportPostInfo = useSelector((state) => state.feed.data.reportPostInfo);
  const isSupportSubmitting = useSelector(
    (state) => state.feed.ui.isSupportSubmitting,
  );
  const supportError = useSelector((state) => state.feed.data.supportError);

  const iconTextStyle = {
    minWidth: '200px',
    display: 'flex',
    justifyContent: 'center',
    fontSize: '26px',
    fontWeight: '300',
    marginTop: '10px',
  };

  const promptTextStyle = {
    fontSize: theme.textFontSizes[2],
    fontWeight: 600,
  };

  const knowledgeBaseTextStyle = {
    fontSize: theme.textFontSizes[1],
    marginBottom: '15px',
    marginTop: '15px',
  };

  const inputLabelTextStyle = {
    marginBottom: '5px',
    fontWeight: 'bold',
  };

  const subjectLine = () => {
    const postType = reportPostInfo.type;
    if (postType === 'Post') {
      return `Reporting Offensive Post (Post ID ${reportPostInfo.postId})`;
    }
    if (postType === 'Comment') {
      return `Reporting Offensive Post (Comment ID ${reportPostInfo.commentId})`;
    }
    return `Reporting Offensive Post (Reply ID ${reportPostInfo.replyId})`;
  };

  // Handler functions
  const handleCloseModal = () => {
    onRequestClose();
    dispatch(resetSubmitSupportError());
    dispatch(resetReportPostInfo());
  };

  const handleSubmitSupport = (values) => {
    dispatch(submitSupport(values));
    feedTracker(
      'Normal Mode',
      'Support modal - report offensive content submitted',
    );
  };

  const handleFormSubmit = (values, { setSubmitting }) => {
    setTimeout(() => {
      handleSubmitSupport(values);
      setSubmitting(false);
    });
  };

  // Render functions
  const renderErrorSection = () => supportError && (
  <ErrorText>{supportError.response.headers.message}</ErrorText>
  );

  const renderForm = (errors, touched, handleChange, values) => (
    <>
      <FormGroup>
        <TitlePlusErrorWrapper>
          <Text styledTextProps={inputLabelTextStyle}>Subject</Text>
          {errors.subject && touched.subject && (
            <FieldError>{errors.subject}</FieldError>
          )}
        </TitlePlusErrorWrapper>
        <Field
          className='form-control'
          id='subject-field'
          name='subject'
          value={subjectLine()}
          disabled
        />
      </FormGroup>
      <FormGroup>
        <TitlePlusErrorWrapper>
          <Text styledTextProps={inputLabelTextStyle}>Message</Text>
          {errors.body && touched.body && (
            <FieldError>{errors.body}</FieldError>
          )}
        </TitlePlusErrorWrapper>
        <Field
          onChange={handleChange}
          name='body'
          component='textarea'
          className='form-control text-area'
          placeholder='Describe your problem or concern here...'
          value={values.body}
        />
      </FormGroup>
    </>
  );

  const renderSubmitButton = (handleSubmit) => (
    <NoHoverButton
      bottom
      fullWidth
      cta={!isSupportSubmitting ? 'Submit' : 'Submitting...'}
      customColor='#10cd8c'
      className='submit-button'
      large
      noBorder
      primary
      square
      disabled={isSupportSubmitting}
      onClick={handleSubmit}
    />
  );

  const renderSupportHeader = () => (
    <SupportTextWrapper>
      <IcomoonReact iconSet={iconSet} size={60} icon='question-mark-circle' />
      <Text styledTextProps={iconTextStyle}>Support</Text>
    </SupportTextWrapper>
  );

  const renderTextSection = () => (
    <>
      <DividerLine />
      <Text styledTextProps={promptTextStyle}>
        Report innappropriate or offensive content using the form below.
      </Text>
      <Text styledTextProps={knowledgeBaseTextStyle}>
        Help us keep this community safe and respectful. Our team will review your message as
        soon as possible.
      </Text>
    </>
  );

  return (
    <ModalWrapper>
      <CloseButtonContainer onClick={handleCloseModal} aria-label='Close modal'>
        <IcomoonReact iconSet={iconSet} size={10} icon='remove' />
      </CloseButtonContainer>
      <ModalContentContainer>
        <Formik
          validationSchema={supportSchema}
          initialValues={{
            subject: subjectLine(),
            body: '',
            page: '/feed',
          }}
          onSubmit={handleFormSubmit}
        >
          {({
            errors, touched, handleSubmit, handleChange, values,
          }) => (
            <Form>
              {renderSupportHeader()}
              {renderTextSection()}
              {renderForm(errors, touched, handleChange, values)}
              {renderErrorSection()}
              {renderSubmitButton(handleSubmit)}
            </Form>
          )}
        </Formik>
      </ModalContentContainer>
    </ModalWrapper>
  );
};

ReportPostModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
};

export default ReportPostModal;
